import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { apiClient } from '@/App';
import { FileText, Car, Building, Star, Check, ArrowRight, Loader2 } from 'lucide-react';

const categoryIcons = {
  contract: FileText,
  radiere: Car,
  declarare: Building,
  pachet: Star,
  standard: FileText
};

const categoryLabels = {
  contract: 'Contract',
  radiere: 'Radiere',
  declarare: 'Declarare',
  pachet: 'Pachet',
  standard: 'Standard'
};

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedServices, setSelectedServices] = useState([]);
  const [exchangeRate, setExchangeRate] = useState(null);

  useEffect(() => {
    fetchServices();
    fetchExchangeRate();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await apiClient.get('/services?website=true');
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchExchangeRate = async () => {
    try {
      const response = await apiClient.get('/exchange-rate');
      setExchangeRate(response.data.eur_ron_rate_with_margin);
    } catch (error) {
      console.error('Error fetching exchange rate:', error);
    }
  };

  const toggleService = (serviceId) => {
    setSelectedServices(prev => 
      prev.includes(serviceId)
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const totalPrice = services
    .filter(s => selectedServices.includes(s.service_id))
    .reduce((sum, s) => sum + s.price, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50">
      {/* Header */}
      <section className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="max-w-2xl">
            <h1 className="font-display text-4xl md:text-5xl font-bold text-slate-900 mb-6" data-testid="services-page-title">
              Serviciile noastre
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Alegeți serviciile de care aveți nevoie. Puteți selecta mai multe servicii 
              și le veți putea comanda împreună.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = categoryIcons[service.category] || FileText;
              const isSelected = selectedServices.includes(service.service_id);
              
              return (
                <Card 
                  key={service.service_id}
                  className={`bg-white rounded-xl border transition-all duration-300 hover:-translate-y-1 cursor-pointer ${
                    isSelected 
                      ? 'border-blue-500 shadow-[0_8px_30px_-4px_rgba(59,130,246,0.2)] ring-2 ring-blue-500/20' 
                      : 'border-slate-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_30px_-4px_rgba(0,0,0,0.1)]'
                  }`}
                  onClick={() => toggleService(service.service_id)}
                  data-testid={`service-card-${index}`}
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          isSelected ? 'bg-blue-600' : 'bg-blue-50'
                        }`}>
                          <Icon className={`h-6 w-6 ${isSelected ? 'text-white' : 'text-blue-600'}`} />
                        </div>
                        <Badge variant="secondary" className="bg-slate-100 text-slate-600">
                          {categoryLabels[service.category]}
                        </Badge>
                      </div>
                      <Checkbox 
                        checked={isSelected}
                        onCheckedChange={() => toggleService(service.service_id)}
                        data-testid={`service-checkbox-${index}`}
                        className="mt-1"
                      />
                    </div>
                    <CardTitle className="text-xl font-semibold text-slate-900 mt-4">
                      {service.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600 leading-relaxed mb-6">
                      {service.description}
                    </p>
                    <div className="flex items-end justify-between">
                      <div>
                        <span className="text-3xl font-bold text-slate-900">
                          {service.price} <span className="text-lg font-medium text-slate-500">EUR</span>
                        </span>
                        {exchangeRate && (
                          <p className="text-sm text-slate-400 mt-0.5" data-testid={`service-price-ron-${index}`}>
                            {(service.price * exchangeRate).toFixed(2)} RON <span className="text-xs">(incl. TVA)</span>
                          </p>
                        )}
                      </div>
                      {isSelected && (
                        <span className="flex items-center gap-1 text-sm font-medium text-blue-600">
                          <Check className="h-4 w-4" />
                          Selectat
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Payment Notice */}
      <section className="py-6 bg-amber-50 border-y border-amber-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-sm text-amber-800 font-medium" data-testid="payment-notice-services">
            Plata se efectuează în RON la cursul BNR din ziua plății +1%.
          </p>
        </div>
      </section>

      {/* Floating Selection Summary */}
      {selectedServices.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] z-40">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-6">
                <div>
                  <span className="text-sm text-slate-500">Servicii selectate:</span>
                  <span className="ml-2 text-lg font-semibold text-slate-900">
                    {selectedServices.length}
                  </span>
                </div>
                <div className="h-8 w-px bg-slate-200 hidden sm:block" />
                <div>
                  <span className="text-sm text-slate-500">Total:</span>
                  <span className="ml-2 text-2xl font-bold text-blue-600">
                    {totalPrice} EUR
                  </span>
                  {exchangeRate && (
                    <span className="ml-2 text-sm text-slate-400">
                      ({(totalPrice * exchangeRate).toFixed(2)} RON)
                    </span>
                  )}
                </div>
              </div>
              <Link 
                to={`/comanda?services=${selectedServices.join(',')}`}
                data-testid="proceed-to-order-btn"
              >
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white shadow-md active:scale-95 transition-all w-full sm:w-auto">
                  Continuă comanda
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default Services;
